import { useCallback, useEffect, useState } from 'react';
import { useDashboard } from '../../../context/dashboardContext';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import ConfirmModal from '../../../components/shareable/confirmModal';
import CustomerTable from './components/customerTable';
import CustomerPagination from '../../../components/shareable/customerPagination';
import UpdateModal from '../../../components/shareable/updateModal';
import FullUpdateModal from './components/fullUpdateModal';
import CreateCustomerModal from './components/createCustomerModal';
import toast from 'react-hot-toast';
import CustomerViewModal from './components/customerViewModal';
import { useSearch } from '../../../context/SearchContext';

const Customers = () => {
  const { getMultipleData, updateCustomer, updateCustomerNotes, updateCustomerFull, deleteCustomer, createCustomer, } = useDashboard();

  const { searchQuery, updateSearch } = useSearch();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [fullUpdateModalOpen, setFullUpdateModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    updateSearch('', 'customers');
    return () => updateSearch('', '');
  }, [updateSearch]);

  const filterCustomers = useCallback((customers, query) => {
    if (!query.trim()) return customers;
    
    const searchTerm = query.toLowerCase().trim();
    return customers.filter(customer => {
      return (
        (customer.customer?.toString().toLowerCase() || '').includes(searchTerm) ||
        (customer.email?.toString().toLowerCase() || '').includes(searchTerm) ||
        (customer.phone?.toString().toLowerCase() || '').includes(searchTerm) ||
        (customer.city?.toString().toLowerCase() || '').includes(searchTerm) ||
        (customer.state?.toString().toLowerCase() || '').includes(searchTerm) ||
        (customer.primary_contact?.toString().toLowerCase() || '').includes(searchTerm)
      );
    });
  }, []);

  const fetchCustomers = useCallback(async () => {
    const res = await getMultipleData(['customers']);
    if (res.success) {
      // Sort customers by name in alphabetical order (A-Z)
      const sortedCustomers = [...res.data].sort((a, b) => {
        const aName = (a.customer || '').toLowerCase();
        const bName = (b.customer || '').toLowerCase();
        return aName.localeCompare(bName);
      });
      setAllCustomers(sortedCustomers);
      const filtered = filterCustomers(sortedCustomers, searchQuery);
      setFilteredCustomers(filtered);
    }
    setLoading(false);
  }, [getMultipleData, searchQuery, filterCustomers]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    if (allCustomers.length > 0) {
      const filtered = filterCustomers(allCustomers, searchQuery);
      setFilteredCustomers(filtered);
      setCurrentPage(1);
    }
  }, [searchQuery, allCustomers, filterCustomers]);

  // Add this function to handle customer creation
  const handleCreateCustomer = async (customerData) => {
    setLoading(true);
    const res = await createCustomer(customerData);
    if (res.success) {
      toast.success('Customer has been created successfully');
      fetchCustomers();
    } else {
      toast.error(res.error || 'Failed to create customer');
      setLoading(false);
    }
  };

  const handleUpdateNotes = (customer) => {
    setSelectedCustomer(customer);
    setNoteModalOpen(true);
  };

  const closeDelete = () => { setDeleteOpen(false); };

  const selectedRow = (row) => {
    setDeleteOpen(true);
    setSelectedCustomer(row);
  };

  const onConfirmingDelete = async () => {
    setLoading(true);
    const res = await deleteCustomer(selectedCustomer._id);
    if (res.success) {
      toast.success('Customer has been deleted')
      fetchCustomers();
    } else {
      toast.error('Customer can not be deleted')
      setLoading(false);
    }
    setDeleteOpen(false);
  };

  const handleUpdateCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setUpdateModalOpen(true);
  };

  const handleSaveNote = async (newNote) => {
    if (!selectedCustomer) return;
    setLoading(true);
    const res = await updateCustomerNotes(selectedCustomer._id, newNote);
    if (res.success) {
      toast.success('Customer note has been saved')
      fetchCustomers();
    } else {
      toast.error('Customer note can not be saved')
      setLoading(false);
    }
  };
  
  const handleSaveName = async (name) => {
    if (!selectedCustomer) return;
    setLoading(true);
    const res = await updateCustomer(selectedCustomer._id, name);
    if (res.success) {
      toast.success('Customer new name has been saved')
      fetchCustomers();
    } else {
      toast.error('Customer name can not be saved')
      setLoading(false);
    }
  };

  const handleAllUpdate = async (customer) => {
    setSelectedCustomer(customer);
    setFullUpdateModalOpen(true);
  };
  
  const handleFullUpdate = async (customerObj) => {
    if (!selectedCustomer) return;
    setLoading(true);
    const res = await updateCustomerFull(selectedCustomer._id, {
      ...customerObj,
      customer: customerObj.customer,
      address: customerObj.address,
      address2: customerObj.address2,
      city: customerObj.city,
      state: customerObj.state,
      zip: customerObj.zip,
      standard_discount: customerObj.standard_discount,
      primary_contact: customerObj.primary_contact,
      title: customerObj.title,
      phone: customerObj.phone,
      email: customerObj.email,
    });
    if (res.success) {
      toast.success('Customer data has been updated')
      fetchCustomers();
    } else {
      toast.error('Customer data can not be updated')
      setLoading(false);
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setViewModalOpen(true);
  };

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) { setCurrentPage(page); }
  };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-6">
      <div className="relative mb-3 mt-3 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Customers
        </h2>
        {/* Add Create Customer Button */}
        <button onClick={() => setCreateModalOpen(true)} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700" > Create Customer </button>
      </div>
      <CustomerTable customers={currentCustomers} onUpdateCustomer={handleUpdateCustomer} onUpdateNotes={handleUpdateNotes} onFullUpdate={handleAllUpdate} onSelectRow={selectedRow} currentPage={currentPage} onViewCustomer={handleViewCustomer} />

      <CustomerViewModal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} customerData={selectedCustomer} />

      <CustomerPagination totalPages={totalPages} currentPage={currentPage} handlePageChange={handlePageChange} totalItems={filteredCustomers.length} itemsPerPage={itemsPerPage} />

      <ConfirmModal onConfirm={onConfirmingDelete} title="Delete Customer" description="Confirm to delete Customer" isOpen={deleteOpen} onClose={closeDelete} />

      <UpdateModal title='Add new Note' placeholder='Enter the text' isOpen={noteModalOpen} onClose={() => setNoteModalOpen(false)} onSave={handleSaveNote} />

      <UpdateModal title='Change Name' placeholder='Enter the name' isOpen={updateModalOpen} onClose={() => setUpdateModalOpen(false)} onSave={handleSaveName} />

      <FullUpdateModal Update='Update Customer' isOpen={fullUpdateModalOpen} onClose={() => setFullUpdateModalOpen(false)} onSave={handleFullUpdate} initialData={selectedCustomer} />

      <CreateCustomerModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} onSave={handleCreateCustomer} />
        
    </div>
  );
};

export default Customers;
